import { useState, useEffect } from 'react';
import { get, set } from 'idb-keyval';

export type OfflineOperationType = 
  | 'CREATE_ORDER' 
  | 'CREATE_TAKEAWAY_ORDER' 
  | 'UPDATE_ORDER_STATUS'
  | 'ADD_ITEMS'
  | 'CREATE_PAYMENT';

export interface QueuedOperation {
  id: string; // uuid
  type: OfflineOperationType;
  payload: any;
  timestamp: string;
  retries: number;
  token?: string;
}

const QUEUE_KEY = 'DufiWaiter_OfflineQueue';

export class OfflineQueueService {
  static async getQueue(): Promise<QueuedOperation[]> {
    try {
      const queue = await get<QueuedOperation[]>(QUEUE_KEY);
      return queue || [];
    } catch {
      return [];
    }
  }

  static async getQueueCount(): Promise<number> {
    const queue = await this.getQueue();
    return queue.length;
  }

  static async saveQueue(queue: QueuedOperation[]): Promise<void> {
    try {
      await set(QUEUE_KEY, queue);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('offline-queue-changed', {
            detail: { count: queue.length },
          })
        );
      }
    } catch (err) {
      console.error('Failed to save offline queue', err);
    }
  }

  static async pushOperation(type: OfflineOperationType, payload: any): Promise<void> {
    const queue = await this.getQueue();
    const token = localStorage.getItem('authToken') || undefined;
    const newOp: QueuedOperation = {
      id: crypto.randomUUID ? crypto.randomUUID() : `op-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type,
      payload,
      timestamp: new Date().toISOString(),
      retries: 0,
      token,
    };
    queue.push(newOp);
    await this.saveQueue(queue);

    // Intentar disparar Background Sync si el navegador lo soporta
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      try {
        const swRegistration = await navigator.serviceWorker.ready;
        await (swRegistration as any).sync?.register('sync-offline-operations');
      } catch (err) {
        // Ignored if sync not supported or denied
      }
    }
  }

  static async removeOperation(id: string): Promise<void> {
    const queue = await this.getQueue();
    const updated = queue.filter(op => op.id !== id);
    await this.saveQueue(updated);
  }

  static async incrementRetry(id: string): Promise<void> {
    const queue = await this.getQueue();
    const idx = queue.findIndex(op => op.id === id);
    if (idx !== -1) {
      queue[idx].retries += 1;
      await this.saveQueue(queue);
    }
  }

  static async clearQueue(): Promise<void> {
    await this.saveQueue([]);
  }
}

/**
 * Hook to reactively track offline queue length and online state
 */
export function useOfflineQueueStatus() {
  const [queueCount, setQueueCount] = useState(0);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  useEffect(() => {
    OfflineQueueService.getQueueCount().then(setQueueCount);

    const handleQueueChange = (e: any) => {
      setQueueCount(e?.detail?.count ?? 0);
    };
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('offline-queue-changed', handleQueueChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('offline-queue-changed', handleQueueChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { queueCount, isOnline };
}
